import { animate } from 'vendor';

const DURATION = 0.35;
const EASE = [0.33, 1, 0.68, 1];

class IngredientsV2 extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('details.ingredients-v2__item'));
    if (this.items.length === 0) return;

    this.allowMultiple = this.hasAttribute('data-allow-multiple');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.busy = false;

    this.items.forEach((item) => {
      item.querySelector('summary')?.addEventListener('click', (event) => this.onSummaryClick(event, item));

      if (item.open) {
        this.setExpanded(item, true);
        const wrap = this.getPanelWrap(item);
        if (wrap) wrap.style.height = 'auto';
      }
    });

    requestAnimationFrame(() => this.classList.add('is-ready'));
  }

  async onSummaryClick(event, item) {
    event.preventDefault();
    if (this.busy) return;

    if (item.classList.contains('is-expanded')) {
      this.busy = true;
      try {
        await this.closeItem(item);
      } finally {
        this.busy = false;
      }
      return;
    }

    this.busy = true;
    try {
      if (!this.allowMultiple) {
        for (const other of this.items.filter(
          (candidate) => candidate !== item && candidate.classList.contains('is-expanded')
        )) {
          await this.closeItem(other);
        }
      }
      await this.openItem(item);
    } finally {
      this.busy = false;
    }
  }

  setExpanded(item, expanded) {
    item.classList.toggle('is-expanded', expanded);
    item.querySelector('summary')?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  getPanelWrap(item) {
    return item.querySelector('.ingredients-v2__panel-wrap');
  }

  async openItem(item) {
    const wrap = this.getPanelWrap(item);
    const inner = wrap?.querySelector('.ingredients-v2__panel-inner');
    if (!wrap || !inner) return;

    item.open = true;
    this.setExpanded(item, true);

    if (this.reducedMotion) {
      wrap.style.height = 'auto';
      return;
    }

    wrap.style.overflow = 'hidden';
    wrap.style.height = '0px';

    await new Promise((resolve) => requestAnimationFrame(resolve));

    const target = inner.scrollHeight;
    await animate(wrap, { height: [`0px`, `${target}px`] }, { duration: DURATION, easing: EASE }).finished;

    wrap.style.height = 'auto';
    wrap.style.overflow = '';
  }

  async closeItem(item) {
    if (!item.classList.contains('is-expanded')) return;

    const wrap = this.getPanelWrap(item);
    const inner = wrap?.querySelector('.ingredients-v2__panel-inner');
    if (!wrap || !inner) {
      this.setExpanded(item, false);
      item.open = false;
      return;
    }

    if (this.reducedMotion) {
      this.setExpanded(item, false);
      item.open = false;
      wrap.style.height = '0px';
      return;
    }

    const start = inner.scrollHeight;
    wrap.style.overflow = 'hidden';
    wrap.style.height = `${start}px`;
    this.setExpanded(item, false);

    await animate(wrap, { height: [`${start}px`, '0px'] }, { duration: DURATION, easing: EASE }).finished;

    wrap.style.height = '0px';
    wrap.style.overflow = '';
    item.open = false;
  }
}

if (!customElements.get('ingredients-v2')) {
  customElements.define('ingredients-v2', IngredientsV2);
}

function initIngredientsScrollCta() {
  document.querySelectorAll('.ingredients-v2[data-cta-action="scroll_up"] [data-scroll-cta]').forEach((button) => {
    if (button.dataset.scrollBound === 'true') return;
    button.dataset.scrollBound = 'true';

    button.addEventListener('click', () => {
      const section = button.closest('.ingredients-v2');
      const selector = section?.dataset.scrollToSelector;
      const target = selector
        ? document.querySelector(selector)
        : document.querySelector('.shopify-section--featured-product .product-info') ||
          document.querySelector('.shopify-section--main-product .product-info') ||
          document.documentElement;

      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
}

initIngredientsScrollCta();
